import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  PlusIcon,
  RectangleStackIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  SparklesIcon,
  CalendarIcon,
  EnvelopeIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AIWorkflows = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // all, active, scheduled, manual, templates
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    workflow_name: '',
    description: '',
    trigger_type: 'manual',
    schedule_cron: '',
    actions: [],
    visible_to_roles: ['all'],
    is_active: true
  });

  const [newAction, setNewAction] = useState({
    type: 'email',
    config: {},
    delay: 0
  });

  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);

  useEffect(() => {
    fetchWorkflows();
    fetchTemplates();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/admin/workflows');
      setWorkflows(response.data.workflows || []);
    } catch (error) {
      logger.error('Error fetching workflows:', error);
      showNotification?.('Failed to load workflows', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get('/api/admin/workflows/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      logger.error('Error fetching templates:', error);
    }
  };

  const createWorkflow = async () => {
    try {
      if (!formData.workflow_name || !formData.description) {
        showNotification?.('Please fill in all required fields', 'error');
        return;
      }

      if (formData.actions.length === 0) {
        showNotification?.('Please add at least one action', 'error');
        return;
      }

      const response = await axiosInstance.post('/api/admin/workflows', formData);

      if (response.data.success) {
        showNotification?.('Workflow created successfully', 'success');
        setShowCreateModal(false);
        resetForm();
        fetchWorkflows();
      }
    } catch (error) {
      logger.error('Error creating workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to create workflow', 'error');
    }
  };

  const updateWorkflow = async () => {
    try {
      if (!selectedWorkflow) return;

      const response = await axiosInstance.put(
        `/api/admin/workflows/${selectedWorkflow._id}`,
        formData
      );

      if (response.data.success) {
        showNotification?.('Workflow updated successfully', 'success');
        setShowEditModal(false);
        resetForm();
        fetchWorkflows();
      }
    } catch (error) {
      logger.error('Error updating workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to update workflow', 'error');
    }
  };

  const deleteWorkflow = async (id) => {
    if (!window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/admin/workflows/${id}`);

      if (response.data.success) {
        showNotification?.('Workflow deleted successfully', 'success');
        fetchWorkflows();
      }
    } catch (error) {
      logger.error('Error deleting workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to delete workflow', 'error');
    }
  };

  const duplicateWorkflow = async (id) => {
    try {
      const response = await axiosInstance.post(`/api/admin/workflows/${id}/duplicate`);

      if (response.data.success) {
        showNotification?.('Workflow duplicated successfully', 'success');
        fetchWorkflows();
      }
    } catch (error) {
      logger.error('Error duplicating workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to duplicate workflow', 'error');
    }
  };

  const toggleWorkflow = async (id, currentStatus) => {
    try {
      const response = await axiosInstance.post(`/api/admin/workflows/${id}/toggle`, {
        is_active: !currentStatus
      });

      if (response.data.success) {
        showNotification?.(
          `Workflow ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
          'success'
        );
        fetchWorkflows();
      }
    } catch (error) {
      logger.error('Error toggling workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to toggle workflow', 'error');
    }
  };

  const executeWorkflow = async (id) => {
    try {
      const response = await axiosInstance.post(`/api/admin/workflows/${id}/execute`);

      if (response.data.success) {
        showNotification?.('Workflow execution started', 'success');
        setTimeout(() => fetchExecutions(id), 1000);
      }
    } catch (error) {
      logger.error('Error executing workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to execute workflow', 'error');
    }
  };

  // Helper to check if workflow has template variables
  const hasTemplateVariables = (workflow) => {
    const varPattern = /\{\{([^}]+)\}\}/g;
    const configStr = JSON.stringify(workflow.actions);
    return varPattern.test(configStr);
  };

  const promptForContext = (workflow) => {
    if (hasTemplateVariables(workflow)) {
      // Workflows with variables cannot be executed manually
      showNotification?.(
        'This workflow contains template variables and can only be triggered by system events (e.g., when a new employee is added). Manual execution is not supported for templated workflows.',
        'warning'
      );
      return;
    }

    // No variables, execute directly
    executeWorkflow(workflow._id);
  };

  const fetchExecutions = async (workflowId) => {
    try {
      setLoadingExecutions(true);
      const response = await axiosInstance.get(`/api/admin/workflows/${workflowId}/executions`);
      setExecutions(response.data.executions || []);
    } catch (error) {
      logger.error('Error fetching executions:', error);
      showNotification?.('Failed to load execution history', 'error');
    } finally {
      setLoadingExecutions(false);
    }
  };

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      showNotification?.('Please describe the workflow you want to create', 'error');
      return;
    }

    try {
      setGeneratingAI(true);
      const response = await axiosInstance.post('/api/admin/workflows/generate', {
        description: aiPrompt
      });

      if (response.data.success) {
        setFormData(response.data.workflow);
        showNotification?.('Workflow generated! Review and save to activate.', 'success');
        setShowAIGenerateModal(false);
        setShowCreateModal(true);
        setAiPrompt('');
      }
    } catch (error) {
      logger.error('Error generating workflow:', error);
      showNotification?.(error.response?.data?.error || 'Failed to generate workflow', 'error');
    } finally {
      setGeneratingAI(false);
    }
  };

  const createFromTemplate = async (templateId) => {
    try {
      const response = await axiosInstance.post(`/api/admin/workflows/from-template/${templateId}`);

      if (response.data.success) {
        showNotification?.('Workflow created from template', 'success');
        fetchWorkflows();
        setActiveTab('all');
      }
    } catch (error) {
      logger.error('Error creating from template:', error);
      showNotification?.(error.response?.data?.error || 'Failed to create from template', 'error');
    }
  };

  const openEditModal = (workflow) => {
    setSelectedWorkflow(workflow);
    setFormData({
      workflow_name: workflow.workflow_name,
      description: workflow.description,
      trigger_type: workflow.trigger_type,
      schedule_cron: workflow.schedule_cron || '',
      actions: workflow.actions || [],
      visible_to_roles: workflow.visible_to_roles || ['all'],
      is_active: workflow.is_active
    });
    setShowEditModal(true);
  };

  const openExecutionModal = (workflow) => {
    setSelectedWorkflow(workflow);
    fetchExecutions(workflow._id);
    setShowExecutionModal(true);
  };

  const resetForm = () => {
    setFormData({
      workflow_name: '',
      description: '',
      trigger_type: 'manual',
      schedule_cron: '',
      actions: [],
      visible_to_roles: ['all'],
      is_active: true
    });
    setNewAction({
      type: 'email',
      config: {},
      delay: 0
    });
    setSelectedWorkflow(null);
  };

  const addAction = () => {
    if (newAction.type === 'email' && !newAction.config.subject) {
      showNotification?.('Please enter email subject', 'error');
      return;
    }

    if (newAction.type === 'calendar') {
      if (!newAction.config.title) {
        showNotification?.('Please enter event title', 'error');
        return;
      }
      if (!newAction.config.start_time) {
        showNotification?.('Please select start date/time', 'error');
        return;
      }

      // Convert datetime-local format to ISO string for backend
      if (newAction.config.start_time && !newAction.config.start_time.includes('T')) {
        // Already in ISO format, no conversion needed
      } else if (newAction.config.start_time) {
        // Convert datetime-local to ISO
        newAction.config.start_time = new Date(newAction.config.start_time).toISOString();
      }
    }

    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { ...newAction }]
    }));

    setNewAction({
      type: 'email',
      config: {},
      delay: 0
    });
  };

  const removeAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const moveAction = (index, direction) => {
    const newActions = [...formData.actions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newActions.length) return;

    [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];

    setFormData(prev => ({
      ...prev,
      actions: newActions
    }));
  };

  const getStatusBadge = (workflow) => {
    if (!workflow.is_active) {
      return { label: 'Inactive', icon: PauseIcon, color: 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300' };
    }
    return { label: 'Active', icon: CheckCircleIcon, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
  };

  const getTriggerIcon = (type) => {
    const icons = {
      manual: PlayIcon,
      scheduled: CalendarIcon,
      event: SparklesIcon
    };
    return icons[type] || PlayIcon;
  };

  const getTriggerLabel = (type) => {
    const labels = {
      manual: 'Manual',
      scheduled: 'Scheduled',
      event: 'Event'
    };
    return labels[type] || type;
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      email: 'Send Email',
      bulk_email: 'Bulk Email Campaign',
      draft_email: 'Create Draft',
      conditional_email: 'Conditional Email',
      smart_followup: 'Smart Follow-up',
      calendar: 'Calendar Event',
      meeting: 'Schedule Meeting',
      user_management: 'User Management',
      create_user: 'Create User',
      update_user: 'Update User',
      deactivate_user: 'Deactivate User',
      task: 'Create Task',
      notification: 'Send Notification',
      delegation: 'Delegate Task',
      drive: 'File Operation'
    };
    return labels[type] || type;
  };

  const filteredWorkflows = workflows.filter(w => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return w.is_active;
    if (activeTab === 'scheduled') return w.trigger_type === 'scheduled';
    if (activeTab === 'manual') return w.trigger_type === 'manual';
    return false;
  });

  if (loading) {
    return (
      <div className="h-full bg-white dark:bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Company Workflows
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {workflows.length} {workflows.length === 1 ? 'workflow' : 'workflows'} • Automate company-wide processes
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAIGenerateModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
              >
                <SparklesIcon className="w-4 h-4" />
                Generate with AI
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Create Workflow
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-6">
            {[
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active' },
              { id: 'scheduled', label: 'Scheduled' },
              { id: 'manual', label: 'Manual' },
              { id: 'templates', label: 'Templates' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'templates' ? (
          // Templates View
          <div>
            {templates.length === 0 ? (
              <div className="text-center py-12">
                <RectangleStackIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No templates available</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Workflow templates will appear here
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <div
                    key={template._id}
                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                          {template.workflow_name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-800">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {template.actions?.length || 0} action{template.actions?.length !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => createFromTemplate(template._id)}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        Use Template
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Workflows List
          <div>
            {filteredWorkflows.length === 0 ? (
              <div className="text-center py-12">
                <RectangleStackIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No workflows yet</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Create your first company-wide workflow to automate processes
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4" />
                  Create Workflow
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredWorkflows.map((workflow) => {
                  const statusBadge = getStatusBadge(workflow);
                  const StatusIcon = statusBadge.icon;
                  const TriggerIcon = getTriggerIcon(workflow.trigger_type);

                  return (
                    <div
                      key={workflow._id}
                      className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                              {workflow.workflow_name || 'Untitled Workflow'}
                            </h3>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadge.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusBadge.label}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              <TriggerIcon className="w-3 h-3" />
                              {getTriggerLabel(workflow.trigger_type)}
                            </span>
                            {workflow.is_company_wide && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                Company-Wide
                              </span>
                            )}
                            {hasTemplateVariables(workflow) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                Event-Triggered
                              </span>
                            )}
                          </div>

                          {workflow.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              {workflow.description}
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                            <span>{workflow.actions?.length || 0} action{workflow.actions?.length !== 1 ? 's' : ''}</span>
                            {workflow.execution_count > 0 && (
                              <span>{workflow.execution_count} execution{workflow.execution_count !== 1 ? 's' : ''}</span>
                            )}
                            {workflow.schedule_cron && (
                              <span>Schedule: {workflow.schedule_cron}</span>
                            )}
                            {workflow.created_at && (
                              <span>Created: {new Date(workflow.created_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          {workflow.is_active && (
                            <button
                              onClick={() => promptForContext(workflow)}
                              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                              title="Execute Now"
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openExecutionModal(workflow)}
                            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                            title="View Executions"
                          >
                            <ChartBarIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleWorkflow(workflow._id, workflow.is_active)}
                            className="p-2 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded-lg transition-colors"
                            title={workflow.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {workflow.is_active ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => openEditModal(workflow)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => duplicateWorkflow(workflow._id)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            title="Duplicate"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteWorkflow(workflow._id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Generate Modal */}
      {showAIGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Generate Workflow with AI
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Describe what you want to automate
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAIGenerateModal(false);
                  setAiPrompt('');
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Example: Send a welcome email to all new employees on their first day, then schedule a one-week follow-up meeting with their manager"
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent resize-none"
              />

              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">Tips for best results:</p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>Be specific about triggers (time-based, event-based, manual)</li>
                  <li>Describe the actions you want to happen</li>
                  <li>Include any conditions or delays between steps</li>
                  <li>Mention who should be involved (roles, specific users)</li>
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => {
                  setShowAIGenerateModal(false);
                  setAiPrompt('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generateWithAI}
                disabled={generatingAI || !aiPrompt.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {generatingAI ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Generate Workflow
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal - Simplified for now, can be expanded */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl max-w-4xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {showEditModal ? 'Edit Workflow' : 'Create Workflow'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Workflow Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.workflow_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, workflow_name: e.target.value }))}
                  placeholder="e.g., New Employee Onboarding"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this workflow does"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Trigger Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trigger Type
                </label>
                <select
                  value={formData.trigger_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, trigger_type: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="manual">Manual (Execute on demand)</option>
                  <option value="scheduled">Scheduled (Cron-based)</option>
                  <option value="event">Event (Triggered by system events)</option>
                </select>
              </div>

              {/* Schedule Cron (if scheduled) */}
              {formData.trigger_type === 'scheduled' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cron Schedule <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schedule_cron}
                    onChange={(e) => setFormData(prev => ({ ...prev, schedule_cron: e.target.value }))}
                    placeholder="0 9 * * * (Daily at 9 AM)"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Format: minute hour day month weekday (e.g., 0 9 * * * = 9 AM daily)
                  </p>
                </div>
              )}

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Visible to Roles
                </label>
                <select
                  multiple
                  value={formData.visible_to_roles}
                  onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData(prev => ({ ...prev, visible_to_roles: values }));
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Users</option>
                  <option value="admin">Admins Only</option>
                  <option value="manager">Managers</option>
                </select>
              </div>

              {/* Actions List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Actions <span className="text-red-500">*</span>
                </label>

                {formData.actions.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No actions added yet. Add actions below to define what this workflow does.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 mb-4">
                    {formData.actions.map((action, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-800"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {index + 1}. {getActionTypeLabel(action.type)}
                          </p>
                          {action.delay > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Delay: {action.delay} minutes
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {index > 0 && (
                            <button
                              onClick={() => moveAction(index, 'up')}
                              className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded"
                              title="Move Up"
                            >
                              ↑
                            </button>
                          )}
                          {index < formData.actions.length - 1 && (
                            <button
                              onClick={() => moveAction(index, 'down')}
                              className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded"
                              title="Move Down"
                            >
                              ↓
                            </button>
                          )}
                          <button
                            onClick={() => removeAction(index)}
                            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title="Remove"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Action Form */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Add New Action</h4>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Action Type
                    </label>
                    <select
                      value={newAction.type}
                      onChange={(e) => setNewAction(prev => ({ ...prev, type: e.target.value, config: {} }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                    >
                      <option value="email">Send Email</option>
                      <option value="bulk_email">Bulk Email Campaign</option>
                      <option value="calendar">Create Calendar Event</option>
                      <option value="task">Create Task</option>
                      <option value="notification">Send Notification</option>
                    </select>
                  </div>

                  {/* Email Action Config */}
                  {newAction.type === 'email' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email Subject
                        </label>
                        <input
                          type="text"
                          value={newAction.config.subject || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, subject: e.target.value }
                          }))}
                          placeholder="Email subject"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email Body
                        </label>
                        <textarea
                          value={newAction.config.body || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, body: e.target.value }
                          }))}
                          placeholder="Email content"
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Recipient Email
                        </label>
                        <input
                          type="email"
                          value={newAction.config.to || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, to: [e.target.value] }
                          }))}
                          placeholder="recipient@example.com"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                        />
                      </div>
                    </>
                  )}

                  {/* Calendar Action Config */}
                  {newAction.type === 'calendar' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Event Title *
                        </label>
                        <input
                          type="text"
                          value={newAction.config.title || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, title: e.target.value }
                          }))}
                          placeholder="Meeting title"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description
                        </label>
                        <textarea
                          value={newAction.config.description || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, description: e.target.value }
                          }))}
                          placeholder="Event description"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white resize-none"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Start Date/Time *
                          </label>
                          <input
                            type="datetime-local"
                            value={newAction.config.start_time || ''}
                            onChange={(e) => setNewAction(prev => ({
                              ...prev,
                              config: { ...prev.config, start_time: e.target.value }
                            }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Duration (minutes)
                          </label>
                          <input
                            type="number"
                            value={newAction.config.duration || 30}
                            onChange={(e) => setNewAction(prev => ({
                              ...prev,
                              config: { ...prev.config, duration: parseInt(e.target.value) || 30 }
                            }))}
                            placeholder="30"
                            min="5"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Attendees (comma-separated emails)
                        </label>
                        <input
                          type="text"
                          value={newAction.config.attendees?.join(', ') || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, attendees: e.target.value.split(',').map(email => email.trim()).filter(Boolean) }
                          }))}
                          placeholder="user1@example.com, user2@example.com"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={newAction.config.location || ''}
                          onChange={(e) => setNewAction(prev => ({
                            ...prev,
                            config: { ...prev.config, location: e.target.value }
                          }))}
                          placeholder="Meeting room or online link"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Delay (minutes)
                    </label>
                    <input
                      type="number"
                      value={newAction.delay}
                      onChange={(e) => setNewAction(prev => ({ ...prev, delay: parseInt(e.target.value) || 0 }))}
                      min="0"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                    />
                  </div>

                  <button
                    onClick={addAction}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-white dark:bg-[#1a1a1a] border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Action
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-800">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Activate workflow immediately</span>
              </label>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={showEditModal ? updateWorkflow : createWorkflow}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {showEditModal ? 'Update Workflow' : 'Create Workflow'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Execution History Modal */}
      {showExecutionModal && selectedWorkflow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Execution History
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {selectedWorkflow.workflow_name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowExecutionModal(false);
                  setSelectedWorkflow(null);
                  setExecutions([]);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {loadingExecutions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
                </div>
              ) : executions.length === 0 ? (
                <div className="text-center py-12">
                  <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No executions yet
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {executions.map((execution) => (
                    <div
                      key={execution._id}
                      className="border border-gray-200 dark:border-gray-800 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              execution.status === 'completed'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : execution.status === 'failed'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : execution.status === 'running'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300'
                            }`}>
                              {execution.status === 'completed' && <CheckCircleIcon className="w-3 h-3" />}
                              {execution.status === 'failed' && <XCircleIcon className="w-3 h-3" />}
                              {execution.status === 'running' && <ClockIcon className="w-3 h-3" />}
                              {execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
                            </span>
                            {execution.execution_type && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {execution.execution_type}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                            <p>Started: {new Date(execution.created_at).toLocaleString()}</p>
                            {execution.end_time && (
                              <p>Ended: {new Date(execution.end_time).toLocaleString()}</p>
                            )}
                            {execution.error_message && (
                              <p className="text-red-600 dark:text-red-400">Error: {execution.error_message}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AIWorkflows;
